import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "context/OnboardingContext";
import { onboardingService } from "services/onboarding-service";
import { useMaterialUIController } from "context";
import { getOnboardingThemeTokens } from "./themeTokens";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Checkbox, Divider, FormControlLabel } from "@mui/material";
import MDButton from "components/MDButton";
import { motion } from "framer-motion";
import OnboardingLayout from "./OnboardingLayout";
import { CircularProgress } from "@mui/material";

const SubscriptionPreview = () => {
  const navigate = useNavigate();
  const { sessionToken, setSubscriptionLines, claimMeta, theme } = useOnboarding();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = getOnboardingThemeTokens({ darkMode, templateTheme: theme });
  const [subscriptions, setSubscriptions] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [proceeding, setProceeding] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const res = await onboardingService.getSubscriptionPreview(sessionToken);
        const responseData = res.data || res;
        const lines = responseData.lines ?? [];
        setSubscriptions(lines);
        setSubscriptionLines(lines);
      } catch (err) {
        console.error("Error fetching subscription preview", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [sessionToken]);

  const handleProceed = () => {
    setProceeding(true);
    setTimeout(() => {
      navigate("/onboard/billing");
    }, 500);
  };

  const total = subscriptions.reduce((acc, sub) => acc + (sub.subtotal || 0), 0);

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress color="info" />
      </MDBox>
    );
  }

  return (
    <OnboardingLayout maxWidth="800px">
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <MDTypography variant="h3" fontWeight="bold" mb={1}>
          {claimMeta?.claim_type === "build_device" ? "Build + Device" : claimMeta?.claim_type === "device" ? "Device" : "Build"} Subscription Preview
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={4}>
          Review your plans and costs before moving to payment.
        </MDTypography>
      </motion.div>

      <MDBox
        mb={4}
        sx={{
          background: t.softSurface,
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${t.borderSubtle}`,
        }}
      >
          <MDBox p={2} display="flex" bgcolor={t.softSurfaceStrong}>
          <MDTypography variant="caption" fontWeight="bold" sx={{ width: "40%", p: 1 }}>
            DEVICE
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" sx={{ width: "35%", p: 1 }}>
            PLAN
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" sx={{ width: "25%", p: 1, textAlign: "right" }}>
            PRICE/MO
          </MDTypography>
        </MDBox>

        {subscriptions.map((sub, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            <MDBox p={2} display="flex" sx={{ borderBottom: `1px solid ${t.divider}` }}>
              <MDTypography variant="body2" fontWeight="medium" sx={{ width: "40%", p: 1 }}>
                {sub.device_name}
              </MDTypography>
              <MDTypography variant="caption" sx={{ width: "35%", p: 1 }}>
                {sub.plan_key}
              </MDTypography>
              <MDTypography variant="body2" sx={{ width: "25%", p: 1, textAlign: "right" }}>
                ${(sub.unit_amount).toFixed(2)}
              </MDTypography>
            </MDBox>
          </motion.div>
        ))}

        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" bgcolor={t.softSurface}>
          <MDTypography variant="h6" fontWeight="bold">
            Total Monthly Cost
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" color="info">
            ${total.toFixed(2)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox mb={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              color="primary"
            />
          }
          label={
            <MDTypography variant="body2" color="text">
              I acknowledge the costs and proceed to payment
            </MDTypography>
          }
        />
      </MDBox>

      <motion.div whileHover={{ scale: acknowledged ? 1.01 : 1 }} whileTap={{ scale: acknowledged ? 0.99 : 1 }}>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleProceed}
          disabled={!acknowledged || proceeding}
          fullWidth
          size="large"
          sx={{ borderRadius: "12px", py: 1.5 }}
        >
          {proceeding ? <CircularProgress size={20} color="white" /> : "Proceed to Payment"}
        </MDButton>
      </motion.div>
    </OnboardingLayout>
  );
};

export default SubscriptionPreview;
